import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { headerLogoBig } from "@gyandiksha/shared-ui/assets";
import { redirectToPortal } from "@gyandiksha/shared-ui/utils";
import { DropdownWrapper, Iconify } from "@repo/design-system/components/ui";
import { useTheme } from "../../contexts/index.jsx";

import { getAuthProps } from "@/services";
import { useQuickServices } from "../../hooks/useQuickServices";

import "./Header.scss";
import NotificationMenu from "./NotificationMenu.jsx";
import UserProfileMenu from "./UserProfileMenu.jsx";

const Header = ({ toggleMobileSidebar, isCollapsed, toggleCollapse, isVisibleHeaderLogo = false }) => {

  const { theme } = useTheme();
  const navigate = useNavigate();
  const userData = getAuthProps();
  const { t } = useTranslation();
  const isCompanyUser = Boolean(userData?.companyId);
  const isErpLoggedIn = Boolean(userData?.isErpLoggedIn);
  const { services: quickServicesList, handleCardClick: handleQuickServiceClick } = useQuickServices({
    skip: isCompanyUser || isErpLoggedIn,
  });

  const handleSwitchPortal = () => {
    window.location.href = redirectToPortal(userData?.isErpLoggedIn, "/portal-selection");
  }

  return (
    <header className="header-container">
      <div className="header-left">
        {isVisibleHeaderLogo &&
          <div className="company-info">
            <div
              className="logo-container header-logo-big"
              role="button"
              tabIndex={0}
              onClick={() => navigate('/dashboard')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate('/dashboard');
                }
              }}
              title="Go to Dashboard"
              aria-label="Go to Dashboard"
            >
              <img src={headerLogoBig} alt="Logo" />
            </div>
          </div>
        }

        {!isVisibleHeaderLogo &&
          <>
            <button className="mobile-toggle" onClick={toggleMobileSidebar}>
              <Iconify icon="lucide:menu" width={24} />
            </button>

            <button className="collapse-toggle" onClick={toggleCollapse}>
              <Iconify ttLabel={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                targetId="sidebar-collapse-toggle"
                icon={isCollapsed ? "lucide:indent-increase" : "lucide:indent-decrease"}
                width={24} />
            </button>

            <div className="gov-branding">
              <div className="gov-text">
                <span className="gov-name mb-2">{userData?.instituteName}</span>
                <span className="gov-dept">{userData?.departmentName}</span>
              </div>
            </div>
          </>
        }
      </div>

      <div className="header-right">
        {!isCompanyUser && (
          <button type="button" className="portal-switch-container"
            onClick={handleSwitchPortal}>
            <Iconify ttLabel="Home" targetId="home-button"
              icon="ion:home" width={20} />
          </button>
        )}

        {!userData?.isErpLoggedIn && !isCompanyUser &&
          <DropdownWrapper toggleElement={
            <div className="portal-switch-container border-none">
              <Iconify ttLabel="Quick Services" targetId="portals-button"
                icon="iconamoon:apps-fill" width={22} />
            </div>}>
            <div className={`header-portal-dropdown ${theme}`}>
              <div className="portal-dropdown-header">
                <Iconify icon="material-symbols:dashboard-rounded" width={20} />
                <span>Quick Services</span>
              </div>

              <div className="portal-dropdown-grid">
                {quickServicesList.map((portal) => (
                  <button type="button" key={portal.key}
                    className={`portal-dropdown-card ${portal.comingSoon ? "coming-soon" : ""}`}
                    onClick={() => handleQuickServiceClick(portal)}>
                    <div className="portal-dropdown-card-icon"
                      style={{ color: portal.color, background: portal.bg, borderColor: portal.bg }}>
                      {portal.icon ? (
                        <Iconify icon={portal.icon} width={28} />
                      ) : portal.img ? (
                        <img src={portal.img} alt={portal.name || portal.title} width={26} height={26} />
                      ) : (
                        <Iconify icon="material-symbols:dashboard-rounded" width={28} />
                      )}
                      {portal.comingSoon && <span className="portal-coming-soon-badge">SOON</span>}
                    </div>
                    <span className="portal-dropdown-card-label" title={portal.title || portal.name}>
                      {portal.title || portal.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </DropdownWrapper>
        }

        {!userData?.isErpLoggedIn && !isCompanyUser && (
          <NotificationMenu
            onViewAllAnnouncements={() => navigate("/view-student-notification")}
          />
        )}
        {/* User Dropdown */}
        <UserProfileMenu />
      </div>

    </header>
  );
};

export default Header;

